// >>> IT #146 — THE ANGELS DRAWER, WHOLE, AND THE R8 CEILING PAID IN THAT LEDGER'S OWN CURRENCY:
// THE THING THAT HAD TO MOVE IS THE THING NOTHING COULD REACH. <<<
//
// Her ticket is about this drawer's ONE control — *"Build your seal with these names"*, which was a
// bare `<a href>` and therefore a FULL PAGE NAVIGATION, so the app remounted, the drawer closed,
// and `picks` (drawer-LOCAL state, the only place those angels lived) went with it. The repair is
// `AngelsDoor`; this is the surface it hangs off, and inside an eleven-thousand-line file the only
// instrument that could ever have read it was a regex.
//
// It holds ten hooks, so it is the `TierCards` / `MicButton` / `SignInModal` class rather than a
// hookless leaf a gate CALLS — **the reach it buys is the IMPORT.** `AngelCard` comes with it
// because the drawer is its only renderer and a type moves with the component that renders it
// (R7/R8's own rule), and every one of its dependencies was already an importable module, so
// nothing cascaded (R8's law: extract opportunistically, never a big-bang rewrite).
import { useEffect, useRef, useState, type FormEvent } from "react";
import { copyText, copyState, copyLabel, copyWords, COPY_FLASH_MS, type CopyState } from "../lib/clipboard";
import { writeJournalEntry, journalNote, JOURNAL_CHANGED_EVENT } from "../lib/journal";
import { type LockInfo } from "../lib/tier-catalog";
import { readAngelDirectory, angelDirectoryNote } from "../lib/angel-directory";
import { t } from "../strings.js";
import { EdgeDrawer } from "./EdgeDrawer";
import { DrawerTab } from "./DrawerTab";
import { MicButton } from "./MicButton";
import { goToSealCreator } from "./AngelsDoor";
import { angelsToSealNames } from "../magicRoom.js";

type AngelEntry = {
  id: string;
  name: string;
  category: string;
  meaning?: string;
  domains?: string[];
  hebrew?: string;
  sign?: string;
  triad?: string;
  sephirah?: string;
  number?: number;
  why?: string;
};
type AngelCategory = { key: string; label: string; blurb: string };

// One angel/divine-name card. Used by both surfaces of the drawer (the match results and
// the browsable directory), so an entry looks and behaves the same either way: its meaning,
// its Hebrew where it serves seal work, and the two controls the owner asked for on EVERY
// entry — Copy, and Send to journal (which lands in the journal quick-access drawer).
function AngelCard({ entry, language }: { entry: AngelEntry; language: string }) {
  const [copied, setCopied] = useState<CopyState>("idle");
  const [sent, setSent] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [sendNote, setSendNote] = useState("");

  const asText = () => {
    const bits = [entry.name];
    if (entry.hebrew) bits.push(entry.hebrew);
    if (entry.sign) bits.push(`Angel of ${entry.sign}`);
    if (entry.triad) bits.push(entry.triad);
    if (entry.sephirah) bits.push(`Sephirah: ${entry.sephirah}`);
    const head = bits.join(" · ");
    const body = entry.domains?.length ? entry.domains.join(", ") : entry.meaning || "";
    return `${head}\n${body}`;
  };
  // R56: the second silent one, and its words came off the catalog in the same edit.
  async function copy() {
    const answer = await copyText(asText());
    setCopied(copyState(answer));
    window.setTimeout(() => setCopied("idle"), COPY_FLASH_MS);
  }
  // R57: the fourth call site, reading the same answer as the other three rather than its own
  // two thirds of one. Its error line carries the server's own words where it sent any.
  async function sendToJournal() {
    setSent("sending");
    const answer = await writeJournalEntry({ title: entry.name, body: asText() });
    if (answer.ok) window.dispatchEvent(new Event(JOURNAL_CHANGED_EVENT));
    setSent(answer.ok ? "sent" : "error");
    setSendNote(journalNote(answer, {
      signIn: t(language, "journal.signIn"),
      failed: t(language, "journal.saveFailed"),
    }));
  }

  return (
    <article className="angel-card">
      <header className="angel-card__head">
        <h4>
          {typeof entry.number === "number" ? <span className="angel-card__num">{entry.number}</span> : null}
          {entry.name}
          {entry.hebrew ? <span className="angel-card__hebrew" lang="he">{entry.hebrew}</span> : null}
        </h4>
        {entry.sign ? <p className="angel-card__tag">Angel of {entry.sign}</p> : null}
        {entry.triad ? <p className="angel-card__tag">{entry.triad}</p> : null}
        {entry.sephirah ? <p className="angel-card__tag">Sephirah: {entry.sephirah}</p> : null}
      </header>
      {entry.why ? <p className="angel-card__why">{entry.why}</p> : null}
      {entry.domains?.length ? (
        <ul className="angel-card__domains">
          {entry.domains.map((d) => <li key={d}>{d}</li>)}
        </ul>
      ) : (
        <p className="angel-card__meaning">{entry.meaning}</p>
      )}
      <div className="angel-card__actions">
        <button type="button" className="button button--ghost" onClick={copy}>{copyLabel(copied, copyWords(language, t))}</button>
        <button type="button" className="button button--ghost" onClick={sendToJournal} disabled={sent === "sending" || sent === "sent"}>
          {sent === "sent" ? "✓ In your journal" : sent === "sending" ? "Sending..." : "Send to journal"}
        </button>
      </div>
      {sent === "error" ? <p className="craft-panel__status" role="status">{sendNote || t(language, "journal.saveFailed")}</p> : null}
    </article>
  );
}

// THE ANGELS DRAWER (AD): the 7th right-rail tab, same shared DrawerTab treatment as the
// other six. A reference database for seal work, spells and the magic room — ask it your
// situation and it names the angels and divine names that fit, or browse and search the
// whole directory by category.
export function AngelsDrawer({ isMember, onRequireMembership, language }: { isMember: boolean; language: string; onRequireMembership: (action: () => void, lock?: LockInfo) => void }) {
  // W4-7: the field the mic writes into, so a long dictation stays visible in it.
  const situationRef = useRef<HTMLTextAreaElement | null>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"ask" | "browse">("ask");
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [picks, setPicks] = useState<AngelEntry[]>([]);
  const [categories, setCategories] = useState<AngelCategory[]>([]);
  const [category, setCategory] = useState("72-angels");
  const [search, setSearch] = useState("");
  // R99: `null` until the read lands (R39's third state). Starting at `[]` is what let a directory
  // that never arrived wear a sentence about her SEARCH.
  const [entries, setEntries] = useState<AngelEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Load the directory when the member opens the browsable side (and whenever they change
  // category or search). The reference itself is free; the match is the gated action.
  useEffect(() => {
    if (!open || view !== "browse") return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const answer = await readAngelDirectory<AngelEntry, AngelCategory>(category, search);
      if (cancelled) return;
      if (answer.categories) setCategories(answer.categories);
      setEntries(answer.entries); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, view, category, search]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!situation.trim()) { setStatus("Tell me your situation first."); return; }
    onRequireMembership(() => { void run(); });
  }
  async function run() {
    setBusy(true); setPicks([]); setMessage(""); setStatus("Finding your angels...");
    try {
      const res = await fetch("/api/angels/match", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus("Sign in to ask this."); return; }
      if (!res.ok || data?.ok === false || !Array.isArray(data?.picks) || !data.picks.length) {
        setStatus(data?.message || "This could not be prepared right now. Please try again."); return;
      }
      setPicks(data.picks); setMessage(data.message || ""); setStatus("");
    } catch { setStatus("This could not be prepared right now. Please try again."); }
    finally { setBusy(false); }
  }

  const activeCategory = categories.find((c) => c.key === category);
  const angelNote = angelDirectoryNote({ entries, categories });

  return (
    <aside className="craft-drawer craft-drawer--angels" aria-label="Angels">
      {/* FREE-1: opens on the ASK view, which fetches nothing (Browse: DECISIONS FREE-1-4). */}
      <DrawerTab label="Angels" onClick={() => setOpen(!open)} />
      <EdgeDrawer open={open} onClose={() => setOpen(false)} label="Angels" className="craft-edge-drawer">
        <div className="craft-panel angels-panel">
          <div className="craft-panel__head">
            <p className="eyebrow">Angels</p>
            <h2>Who to call on</h2>
            <p className="craft-panel__blurb">The angels, the choirs, the angels of the signs, and the names of God, with their meanings, for your seal work and your spells.</p>
          </div>

          <div className="angels-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={view === "ask"} className={view === "ask" ? "angels-tab is-active" : "angels-tab"} onClick={() => setView("ask")}>Ask</button>
            <button type="button" role="tab" aria-selected={view === "browse"} className={view === "browse" ? "angels-tab is-active" : "angels-tab"} onClick={() => setView("browse")}>Browse</button>
          </div>

          {view === "ask" ? (
            <>
              <form className="craft-panel__form" onSubmit={submit}>
                <label className="craft-panel__field">
                  <span>Tell me your situation</span>
                  <textarea ref={situationRef} value={situation} onChange={(e) => setSituation(e.target.value)} placeholder="What are you facing? Protection, a court case, money that will not move, someone you need to reach..." rows={4} />
                </label>
                <div className="craft-panel__controls">
                  <MicButton setValue={setSituation} field={situationRef} />
                  <button className={busy ? "button button--primary is-generating" : "button button--primary"} type="submit" disabled={busy}>
                    {busy ? "Looking..." : "Who do I call on?"}
                  </button>
                </div>
              </form>
              {status ? <p className="craft-panel__status">{status}</p> : null}
              {message ? <p className="angels-message">{message}</p> : null}
              {picks.length ? <div className="angels-list">{picks.map((e) => <AngelCard key={e.id} entry={e} language={language} />)}</div> : null}
              {/* N3-2: the directory law's whole point. She names the angel and its purpose, then the
                  member goes and builds the seal themselves. The route is a real control, not a hint
                  buried in a paragraph. */}
              {picks.length ? (
                <button
                  type="button"
                  className="button button--primary angels-route"
                  onClick={() => goToSealCreator(angelsToSealNames(picks))}
                >
                  Build your seal with {picks.length === 1 ? "this name" : "these names"}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <div className="angels-browse__controls">
                <input
                  className="angels-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or by what you need..."
                  aria-label="Search the angels"
                />
                <div className="angels-cats">
                  {categories.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={c.key === category ? "angels-cat is-active" : "angels-cat"}
                      onClick={() => setCategory(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {activeCategory ? <p className="angels-browse__blurb">{activeCategory.blurb}</p> : null}
              {loading ? <p className="craft-panel__status">Opening the directory...</p> : null}
              {/* R99: ONE sentence, chosen by the law, so an unread directory is never an empty one. */}
              {!loading && angelNote ? <p className="craft-panel__status" role="status">{angelNote}</p> : null}
              <div className="angels-list">{(entries || []).map((e) => <AngelCard key={e.id} entry={e} language={language} />)}</div>
            </>
          )}
        </div>
      </EdgeDrawer>
    </aside>
  );
}

// The three conjure drawers (Q5): stacked on the RIGHT rail below Journal + Verse, same
// edge-tab treatment as the rest (moved off the left edge where they overlapped the side menu).
